import {
  ReactNode,
  Suspense,
  createContext,
  lazy,
  useContext,
  useMemo,
} from 'react';
import { useUtilityDrawer } from '../utility-drawer/utility-drawer.hooks';
import { QuickCreateSource } from './quick-create-communication.types';

// Lazy so importing this context (for the open hook) from the entry points
// doesn't pull the heavy offer/message payload builders + schemas into their
// module graph — the drawer chunk loads only when it first renders.
const QuickCreateCommunicationDrawer = lazy(
  () => import('./quick-create-communication-drawer.component'),
);

type QuickCreateCommunicationContextValue = {
  /** Open the Quick-Create Communication drawer for the given entry-point source. */
  openQuickCreateCommunication: (source: QuickCreateSource) => void;
};

const QuickCreateCommunicationContext =
  createContext<QuickCreateCommunicationContextValue | null>(null);

/**
 * Scopes the Quick-Create Communication drawer to a subtree (mounted once at the
 * Guest Journey page root) so any tab — segment grids, retention chart — can open
 * it with a targeting source without local wiring.
 */
export const QuickCreateCommunicationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { open, close, setDrawerData, setOpen, drawerData } =
    useUtilityDrawer<QuickCreateSource>();

  const value = useMemo(
    () => ({
      openQuickCreateCommunication: (source: QuickCreateSource) => {
        setDrawerData(source);
        setOpen(true);
      },
    }),
    [setDrawerData, setOpen],
  );

  return (
    <QuickCreateCommunicationContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        <QuickCreateCommunicationDrawer
          open={open}
          close={close}
          source={drawerData}
        />
      </Suspense>
    </QuickCreateCommunicationContext.Provider>
  );
};

export const useQuickCreateCommunication = () => {
  const ctx = useContext(QuickCreateCommunicationContext);
  if (!ctx) {
    throw new Error(
      'useQuickCreateCommunication must be used within a QuickCreateCommunicationProvider',
    );
  }
  return ctx;
};

/**
 * Non-throwing variant: returns `null` when no provider is present (e.g. a
 * component rendered outside Guest Journey, or in a unit test), so the entry
 * points can stay additive — the quick-action simply doesn't render.
 */
export const useQuickCreateCommunicationOptional = () =>
  useContext(QuickCreateCommunicationContext);
